import styled, { css, keyframes } from 'styled-components';
import {
  CHIP_MENU_ACTIVE_TREATMENT_SUBTLE,
  CHIP_MENU_ALIGN_CENTER,
  CHIP_MENU_ALIGN_RIGHT,
  CHIP_MENU_ITEM_TONE_ACCENT,
  CHIP_MENU_ITEM_TONE_ARCHIVE,
  CHIP_MENU_ITEM_TONE_DANGER,
  CHIP_MENU_ITEM_TONE_GHOST,
  CHIP_MENU_ITEM_VARIANT_BUTTON,
  CHIP_MENU_PLACEMENT_TOP,
  type ChipMenuActiveTreatment,
  type ChipMenuAlign,
  type ChipMenuPlacement,
  type ChipMenuItemTone,
  type ChipMenuItemVariant,
} from './ChipMenu.constants';

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Centre-aligned variant. The Panel statically lives at
// `transform: translateX(-50%)` (so its midpoint sits at the anchor's
// midpoint) — this keyframe preserves that translateX while running the
// vertical slide so the dropdown doesn't jump sideways when it opens.
const slideInCentered = keyframes`
  from { opacity: 0; transform: translate(-50%, -4px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

export const Panel = styled.div<{
  $align: ChipMenuAlign;
  $placement: ChipMenuPlacement;
  $portal?: boolean;
}>`
  position: ${({ $portal }) => ($portal ? 'fixed' : 'absolute')};
  ${({ $portal, $placement }) => {
    if ($portal) return '';
    return $placement === CHIP_MENU_PLACEMENT_TOP
      ? css`
          bottom: calc(100% + 6px);
        `
      : css`
          top: calc(100% + 6px);
        `;
  }}
  min-width: 220px;
  padding: 8px;
  border-radius: 22px;
  z-index: 60;
  animation: ${({ $align }) =>
      $align === CHIP_MENU_ALIGN_CENTER ? slideInCentered : slideIn}
    ${({ theme: { motion } }) => motion?.duration?.fast ?? '140ms'}
    ${({ theme: { motion } }) =>
      motion?.easing?.emphasised ?? 'cubic-bezier(0.2,0.8,0.2,1)'};

  ${({ theme: { colors = {}, elevation } }) => css`
    background: ${colors.surfaceContainer ?? '#1F1F27'};
    border: 1px solid ${colors.outlineVariant ?? '#464555'};
    box-shadow: ${elevation?.modal ?? '0 16px 40px rgba(0, 0, 0, 0.55)'};
  `}

  ${({ $align, $portal }) => {
    // In portal mode top/left/right come from the inline style computed
    // off the anchor's viewport rect; only the centre transform is still
    // needed here (its translateX can't be expressed as a coordinate).
    if ($align === CHIP_MENU_ALIGN_RIGHT) {
      return $portal
        ? ''
        : css`
            right: 0;
            left: auto;
          `;
    }
    if ($align === CHIP_MENU_ALIGN_CENTER) {
      return $portal
        ? css`
            transform: translateX(-50%);
          `
        : css`
            left: 50%;
            right: auto;
            transform: translateX(-50%);
          `;
    }
    return $portal
      ? ''
      : css`
          left: 0;
        `;
  }}
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const HeaderCap = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.02em;
  text-transform: none;
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
    background: color-mix(in oklch, ${colors.onSurface ?? '#E4E1ED'} 6%, transparent);
  `}
`;

export const Separator = styled.div`
  height: 1px;
  background: ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#464555'};
  margin: 6px 4px;
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-height: 280px;
  overflow-y: auto;
`;

const itemToneCss = ($tone: ChipMenuItemTone) => css`
  ${({ theme: { colors = {} } }) => {
    if ($tone === CHIP_MENU_ITEM_TONE_GHOST) {
      return css`
        color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
        & > .chip-menu-item__icon {
          color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
        }
      `;
    }
    if ($tone === CHIP_MENU_ITEM_TONE_ACCENT) {
      return css`
        color: ${colors.primary ?? '#C1C1FF'};
        & > .chip-menu-item__icon {
          color: ${colors.primary ?? '#C1C1FF'};
        }
      `;
    }
    if ($tone === CHIP_MENU_ITEM_TONE_DANGER) {
      const danger = colors.error ?? '#f2a6a0';
      return css`
        color: ${danger};
        & > .chip-menu-item__icon {
          color: ${danger};
        }
      `;
    }
    if ($tone === CHIP_MENU_ITEM_TONE_ARCHIVE) {
      const archive = colors.caution ?? '#f5c04b';
      return css`
        color: ${archive};
        & > .chip-menu-item__icon {
          color: ${archive};
        }
      `;
    }
    return css`
      color: ${colors.onSurface ?? '#E4E1ED'};
      & > .chip-menu-item__icon {
        color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
      }
    `;
  }}
`;

// Filled-pill treatment for `variant="button"`. The tone's colour drives
// the *background* (instead of the text colour) and a contrast colour is
// used for the label/icon. We use the dedicated `primaryEmphasis` /
// `onPrimaryEmphasis` token pair for accent — they're the theme-fixed
// emphasis variant designed for components that look the same in
// light/dark themes (the menu surface is dark in both). Hover prefers
// `primaryEmphasisHover` when present (pixel-perfect to the design
// handoff) and falls back to a state-layer mix otherwise.
const itemButtonCss = ($tone: ChipMenuItemTone) => css`
  ${({ theme: { colors = {} } }) => {
    if ($tone === CHIP_MENU_ITEM_TONE_DANGER) {
      const bg = colors.errorContainer ?? '#93000A';
      const fg = colors.onErrorContainer ?? '#FFDAD6';
      return css`
        background: ${bg};
        color: ${fg};
        & > .chip-menu-item__icon {
          color: ${fg};
        }
        &:hover:not(:disabled) {
          background: color-mix(in oklch, ${bg} 92%, ${fg});
        }
      `;
    }
    if ($tone === CHIP_MENU_ITEM_TONE_ARCHIVE) {
      const bg = colors.cautionContainer ?? '#F9BC00';
      const fg = colors.onCautionContainer ?? colors.onCaution ?? '#3F2E00';
      return css`
        background: ${bg};
        color: ${fg};
        & > .chip-menu-item__icon {
          color: ${fg};
        }
        &:hover:not(:disabled) {
          background: color-mix(in oklch, ${bg} 92%, ${fg});
        }
      `;
    }
    // Default + ghost + accent all use the primary-emphasis pair —
    // the pixel-perfect lavender from the design handoff. Hover uses
    // the hand-tuned hover token when present; otherwise mixes the
    // foreground in at 8% (Material's standard state-layer recipe).
    const bg = colors.primaryEmphasis ?? '#CFC9F5';
    const fg = colors.onPrimaryEmphasis ?? '#1E1A8A';
    const hover =
      colors.primaryEmphasisHover ??
      `color-mix(in oklch, ${bg} 92%, ${fg})`;
    return css`
      background: ${bg};
      color: ${fg};
      & > .chip-menu-item__icon {
        color: ${fg};
      }
      &:hover:not(:disabled) {
        background: ${hover};
      }
    `;
  }}
`;

interface ItemProps {
  $tone: ChipMenuItemTone;
  $variant: ChipMenuItemVariant;
  $isActive?: boolean;
  $activeTreatment: ChipMenuActiveTreatment;
}

export const Item = styled.button<ItemProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: 0;
  font-size: 13.5px;
  font-weight: 500;
  border-radius: ${({ theme: { borderRadius = {} } }) => borderRadius.full ?? '100px'};
  cursor: pointer;
  text-align: left;

  ${({ $tone, $variant }) =>
    $variant === CHIP_MENU_ITEM_VARIANT_BUTTON ? itemButtonCss($tone) : itemToneCss($tone)}

  /* Pill-button rows render as discrete buttons. We subtract the 4px
     margin from the inherited 100% width (with box-sizing: border-box)
     so the pill stays inside the panel's content area instead of
     overflowing — older Chromium accepts width:100% + margin together,
     but it's safer to spell it out. Matches the design's
     chip-menu__item--accent override (margin 4px, padding 9px 12px,
     font-weight 600). */
  ${({ $variant }) =>
    $variant === CHIP_MENU_ITEM_VARIANT_BUTTON &&
    css`
      box-sizing: border-box;
      width: calc(100% - 8px);
      margin: 4px;
      padding: 9px 12px;
      font-weight: 600;
    `}

  /* Default hover (flat row) — only applies to non-button items.
     Button-variant rows define their own hover background inside
     itemButtonCss (using primaryEmphasisHover etc.). Without this
     guard the later, generic rule overrides the variant rule and
     the pill flashes to the menu surface colour on hover, leaving
     the dark indigo label looking like blue text on grey. */
  ${({ $variant, theme: { colors = {} } }) =>
    $variant !== CHIP_MENU_ITEM_VARIANT_BUTTON &&
    css`
      &:hover:not(:disabled) {
        background: ${colors.surfaceContainerHigh ?? '#292932'};
      }
    `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  & > .chip-menu-item__icon > svg,
  & > .chip-menu-item__icon > .material-symbols-rounded {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }

  ${({ $isActive, $activeTreatment, $variant, theme: { colors = {} } }) => {
    if (!$isActive || $variant === CHIP_MENU_ITEM_VARIANT_BUTTON) return '';
    if ($activeTreatment === CHIP_MENU_ACTIVE_TREATMENT_SUBTLE) {
      // The active row keeps a transparent background but its text +
      // icon switch to the primary-emphasis lavender (`#CFC9F5`) so
      // the selection reads as a single coloured row against the
      // white/base inactive rows. Pixel-perfect to the design's
      // `.chip-menu.proc-crumb-menu .chip-menu__item.is-active`.
      const emphasis = colors.primaryEmphasis ?? '#CFC9F5';
      const onSurface = colors.onSurface ?? '#E4E1ED';
      return css`
        background: transparent;
        color: ${emphasis};
        & > .chip-menu-item__icon {
          color: ${emphasis};
        }
        &:hover:not(:disabled) {
          background: color-mix(in oklch, ${onSurface} 6%, transparent);
        }
      `;
    }
    const primary = colors.primary ?? '#C1C1FF';
    return css`
      color: ${primary};
      background: color-mix(in oklch, ${primary} 12%, transparent);
      & > .chip-menu-item__icon {
        color: ${primary};
      }
    `;
  }}

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: -2px;
  }
`;

export const ItemMeta = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;
